import { Link } from "react-router-dom";
import { Collapsible } from "@/shared/ui/Collapsible";
import type { Order } from "../../types";
import { deriveMailStatusFromRaw, orderHasAllServiceSku } from "../../allserviceApi";
import { deriveWfMailStatusFromRaw, verifyWebFurnitureMailSent } from "../../webfurnitureApi";
import { verifyPickupMailSent } from "../../pickupMailApi";
import { MailStatusBadge } from "../MailStatusBadge";
import { BTN } from "./stile";
// Stessa fonte della card: helper ora esportato da OrderCard (review P2-T3),
// niente mirror locale che potrebbe driftare.
import { pickupMailStatusFor } from "../OrderCard";

/** Attività e mail dell'ordine: stati invii AS/WF/pickup dai flag raw
 *  (stessi deriver della card) + link al thread mail quando esiste.
 *
 *  ⚠️ Niente da mostrare → la sezione NON si renderizza. Era l'unica del rail a
 *  stampare comunque «Nessuna attività registrata»: una riga di rumore su ogni
 *  ordine che non ha ancora nessun invio, cioè la maggioranza. */
export function ActivitySection({ order }: { order: Order }) {
  const asStatus = orderHasAllServiceSku(order) ? deriveMailStatusFromRaw(order.raw) : null;
  const wfStatus =
    order.raw?.wf_mail_app_sent_at != null ? deriveWfMailStatusFromRaw(order.raw) : null;
  // Aurora Bedding scrive nei suoi campi (``aurora_mail_*``): un badge per
  // fornitore, o l'invio dell'uno racconterebbe quello dell'altro.
  const auroraStatus =
    order.raw?.aurora_mail_app_sent_at != null
      ? deriveWfMailStatusFromRaw(order.raw, "aurora")
      : null;
  // Mauro Ferretti: l'ordine parte via portale, ma lo stato vive negli stessi
  // campi (``mf_mail_*``) — quindi il badge è lo stesso degli altri.
  // ponytail: terza copia dello stesso blocco; alla quarta si passa a un map
  // sui profili invece di aggiungerne un'altra.
  const mfStatus =
    order.raw?.mf_mail_app_sent_at != null
      ? deriveWfMailStatusFromRaw(order.raw, "mauroferretti")
      : null;
  const pickupStatus = pickupMailStatusFor(order);
  const voci = [
    asStatus,
    wfStatus,
    auroraStatus,
    mfStatus,
    pickupStatus,
    order.hasMail || null,
  ].filter(Boolean).length;
  if (voci === 0) return null;
  return (
    <Collapsible
      id="attivita"
      title="Attività e mail"
      area="tempo"
      summary={`· ${voci} ${voci === 1 ? "voce" : "voci"}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {asStatus && <MailStatusBadge status={asStatus} orderId={order.id} variant="expanded" />}
        {wfStatus && (
          <MailStatusBadge
            status={wfStatus}
            orderId={order.id}
            variant="expanded"
            onVerify={verifyWebFurnitureMailSent}
            testIdSuffix="-wf"
          />
        )}
        {auroraStatus && (
          <MailStatusBadge
            status={auroraStatus}
            orderId={order.id}
            variant="expanded"
            onVerify={(orderId) => verifyWebFurnitureMailSent(orderId, "aurora")}
            testIdSuffix="-aurora"
          />
        )}
        {mfStatus && (
          <MailStatusBadge
            status={mfStatus}
            orderId={order.id}
            variant="expanded"
            onVerify={(orderId) => verifyWebFurnitureMailSent(orderId, "mauroferretti")}
            testIdSuffix="-mauroferretti"
          />
        )}
        {pickupStatus && (
          <MailStatusBadge
            status={pickupStatus}
            orderId={order.id}
            variant="expanded"
            onVerify={verifyPickupMailSent}
            testIdSuffix="-pickup"
          />
        )}
        {order.hasMail && (
          <Link to={`/mail/${encodeURIComponent(order.id)}`} className={BTN}>
            Apri thread mail
          </Link>
        )}
      </div>
    </Collapsible>
  );
}
